'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { CreditCard, Repeat, /* Star, */ Trash2 } from 'lucide-react';
import { PaymentPaymentMethodEnum, PaymentPaymentStatusEnum, PopulatedOrder } from '@/lib/Apis';
import { formatPrice, formatDate } from '@/lib/utils/format';
import { Badge } from './ui/Badge';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { useCart } from '@/providers/CartProvider';
import { useStore } from '@/context/StoreContext';
import { useOrderProductData } from '@/hooks/useOrderProductImages';

interface OrderCardProps {
  order: PopulatedOrder;
  onDelete?: (id: string) => void;
}

export function OrderCard({ order, onDelete }: OrderCardProps) {
  const router = useRouter();
  const { buildPath } = useBasePath();
  const { reorderFromOrder, isLoading: isCartLoading } = useCart();
  const { setCartOpen } = useStore();
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const { images: productImages, names: productNames } = useOrderProductData(order.items);
  const dragX = useMotionValue(0);

  // Create an opacity transformation based on the drag distance
  const deleteOpacity = useTransform(dragX, [-100, -20], [1, 0]);

  const config = order.orderStatus;
  const customProducts = (order.customProducts || []) as any[];
  const itemCount = (order.items?.length || 0) + customProducts.length;
  const showPriceBreakdown = (order.shippingCost && order.shippingCost > 0) ||
    (order.tax && order.tax > 0) ||
    (order.discountedAmount && order.discountedAmount > 0);

  const isCompletedOrder = order.orderStatus &&
    ['Delivered', 'Picked Up', 'Fulfilled'].includes(order.orderStatus);

  // Reviews are temporarily hidden.
  // const handleReviewClick = (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   router.push(buildPath('/reviews'));
  // };

  const handleReorderClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReordering(true);
    try {
      await reorderFromOrder(order);
      setCartOpen(true);
    } finally {
      setIsReordering(false);
    }
  };

  const handleCardClick = () => {
    if (isDragging) return;
    router.push(buildPath(`/account/orders/${order._id || order.id}`));
  };

  return (
    <div className="relative overflow-hidden rounded-lg bg-slate-100">
      {/* Delete Background Layer */}
      <motion.div
        style={{ opacity: deleteOpacity }}
        className="absolute inset-0 bg-red-600 flex items-center justify-end px-8"
      >
        <div className="flex flex-col items-center gap-1 text-white">
          <Trash2 className="w-6 h-6" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Delete</span>
        </div>
      </motion.div>

      {/* Swipeable Foreground Card */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -100, right: 0 }}
        dragElastic={0.05}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(_, info) => {
          // Reset dragging state after a short delay to prevent accidental click
          setTimeout(() => setIsDragging(false), 50);

          if (info.offset.x < -70 && onDelete && (order._id || order.id)) {
            onDelete((order._id || order.id) as string);
          }
        }}
        style={{ x: dragX }}
        whileTap={{ cursor: 'grabbing' }}
        className="relative z-10 bg-white border border-slate-200 p-4 shadow-xs hover:shadow-md transition-shadow cursor-pointer touch-pan-y"
        onClick={handleCardClick}
      >
        {/* Header - Compact */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          {/* Small screens: stacked rows */}
          <div className="sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-3 flex flex-col gap-2">
            {/* Top row: Order number + Badge */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900 group-hover:text-hprimary transition-colors">
                  #{((order._id || order.id || '').slice(-8)).toUpperCase()}
                </h3>
                <Badge variant={config as any} className="sm:hidden">{config}</Badge>
              </div>
              <p className="text-lg font-bold text-slate-900 sm:hidden">{formatPrice(order.grandTotal || 0)}</p>
            </div>
            {/* Middle row: Date and items */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {formatDate(order.createdAt || new Date(), 'short')}
              </span>
              {itemCount > 0 && (
                <span className="text-xs text-gray-500">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
              )}
            </div>
            {/* Bottom row: Price and badge (desktop) */}
            <div className="hidden sm:flex items-center justify-between">
              <Badge variant={config as any}>{config}</Badge>
              <p className="text-lg font-bold text-slate-900">{formatPrice(order.grandTotal || 0)}</p>
            </div>
          </div>
        </div>

        {/* Items List - Compact */}
        <div className="space-y-2 mb-4">
          {itemCount > 0 ? (
            <>
              {order.items && order.items.slice(0, 3).map((item) => {
                const itemPrice = item.productVariantData?.finalPrice || 0;
                const itemTotal = itemPrice * item.quantity;
                const variantImg = item?.productVariantData?.media?.[0]?.file;
                const isPlaceholder = !variantImg || variantImg === '/placeholder.png';
                const productId = (item.productVariantData as any).productId;
                const itemImage = isPlaceholder
                  ? (productImages[productId] || PLACEHOLDER_IMAGE_SRC)
                  : variantImg;

                return (
                  <div key={item.productVariantId || item._id} className="flex items-center gap-2 text-sm">
                    <div className="relative w-12 h-12 rounded-sm bg-gray-100 shrink-0 overflow-hidden">
                      <Image
                        src={itemImage}
                        alt={item?.productVariantData?.name || 'Product image'}
                        fill
                        className="object-cover"
                        sizes="48px"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      {(() => {
                        const productId = (item.productVariantData as any).productId;
                        const productName = item.productVariantData?.productName || productNames[productId];
                        const variantName = item.productVariantData?.name;
                        const displayName = productName || variantName || 'Unknown Product';
                        const showVariant = variantName && productName && variantName !== productName;
                        return (
                          <>
                            <p className="font-medium text-slate-900 truncate text-sm">{displayName}</p>
                            {showVariant && <p className="text-xs text-gray-500 truncate">{variantName}</p>}
                          </>
                        );
                      })()}
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{formatPrice(itemTotal)}</p>
                  </div>
                );
              })}
              {customProducts.slice(0, Math.max(0, 3 - (order.items?.length || 0))).map((cp: any, idx: number) => {
                const name = typeof cp === 'string' ? cp : (cp.name || 'Custom Item');
                const qty = typeof cp === 'object' ? (cp.quantity || 1) : 1;
                const price = typeof cp === 'object' ? (cp.price || 0) : 0;
                return (
                  <div key={`custom-${idx}`} className="flex items-center gap-2 text-sm">
                    <div className="relative w-12 h-12 rounded-sm bg-gray-100 shrink-0 overflow-hidden">
                      <Image
                        src={PLACEHOLDER_IMAGE_SRC}
                        alt={name}
                        fill
                        className="object-cover"
                        sizes="48px"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm">{name}</p>
                      <p className="text-xs text-gray-500">Qty: {qty}</p>
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{formatPrice(price * qty)}</p>
                  </div>
                );
              })}
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">No items found</p>
          )}
          {itemCount > 3 && (
            <p className="text-xs text-gray-500 text-center pt-1">
              +{itemCount - 3} more {itemCount - 3 === 1 ? 'item' : 'items'}
            </p>
          )}
        </div>

        {/* Price Breakdown - Only if needed */}
        {showPriceBreakdown && (
          <div className="mb-4 pb-4 border-b border-gray-200 space-y-1 text-xs">
            {order.shippingCost !== undefined && order.shippingCost > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>{formatPrice(order.shippingCost)}</span>
              </div>
            )}
            {order.tax !== undefined && order.tax > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span>{formatPrice(order.tax)}</span>
              </div>
            )}
            {order.discountedAmount !== undefined && order.discountedAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-{formatPrice(order.discountedAmount)}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 font-medium">
          {order.payment?.paymentStatus !== PaymentPaymentStatusEnum.Paid &&
            order.payment?.paymentMethod === PaymentPaymentMethodEnum.Card && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(order?.payment?.hostedInvoiceUrl || '', '_blank');
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-hprimary-500 bg-hprimary-500 hover:bg-hprimary-600 text-white px-4 py-2 text-sm transition-colors"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <CreditCard className="w-4 h-4" />
                Pay Now
              </button>
            )}

          {isCompletedOrder && (
            <>
              <button
                onClick={handleReorderClick}
                disabled={isReordering || isCartLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-hprimary-500 bg-white hover:bg-hprimary-500/5 text-hprimary-500 px-4 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Repeat className="w-4 h-4" />
                {isReordering ? 'Adding...' : 'Reorder'}
              </button>
              {/* Reviews are temporarily hidden.
              <button
                onClick={handleReviewClick}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-haccent-500 bg-white hover:bg-haccent-500/5 text-haccent-500 px-4 py-2 text-sm transition-colors"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Star className="w-4 h-4" />
                Write Review
              </button>
              */}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
